import { API_BASE_URL } from "./config";
import { ApiError } from "./types";

const API_PREFIX = "/api/v1";
const TOKEN_KEY = "kameti.auth.token";
const REFRESH_TOKEN_KEY = "kameti.auth.refreshToken";

export const authStorage = {
  get(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  set(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  },
  getRefresh(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  setRefresh(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    clearHttpCache();
  },
};

/** Backend envelope: { success: false, error: { code, message, field } }. */
interface BackendErrorPayload {
  success?: false;
  error?: { code?: string; message?: string; field?: string | null };
}

function normalizeMessage(payload: unknown, fallback: string): string {
  const error = (payload as BackendErrorPayload | null)?.error;
  if (error && typeof error.message === "string") return error.message;
  return fallback;
}

function normalizeCode(payload: unknown): string {
  const error = (payload as BackendErrorPayload | null)?.error;
  if (error && typeof error.code === "string") return error.code;
  return "REQUEST_FAILED";
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  /** Attach the stored auth token automatically. Default true. */
  withAuth?: boolean;
  /** Return the full { success, data, meta } envelope instead of unwrapping .data — list endpoints need `meta`. */
  raw?: boolean;
  /** Internal — set when this call is a post-refresh retry, to prevent looping. */
  _isRetry?: boolean;
}

let refreshInFlight: Promise<boolean> | null = null;

/** Exchanges the stored refresh token for a new token pair. De-duplicated so concurrent 401s don't each fire their own refresh call. */
async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = authStorage.getRefresh();
  if (!refreshToken) return false;

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}${API_PREFIX}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ refreshToken }),
        });
        if (!response.ok) return false;
        const payload = (await response.json()) as { data?: { accessToken?: string; refreshToken?: string } };
        if (!payload.data?.accessToken || !payload.data?.refreshToken) return false;
        authStorage.set(payload.data.accessToken);
        authStorage.setRefresh(payload.data.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

interface RawOptions {
  method: string;
  headers: Record<string, string>;
  body?: string | FormData;
  path: string;
  withAuth: boolean;
  isRetry: boolean;
}

const inflightGets = new Map<string, Promise<unknown>>();
const responseCache = new Map<string, { payload: unknown; timestamp: number }>();
const CACHE_TTL_MS = 1500;

export function clearHttpCache(): void {
  responseCache.clear();
  inflightGets.clear();
}

/** Internal fetch execution with 401 token refresh retry. */
async function executeRawRequest(options: RawOptions): Promise<unknown> {
  const { method, headers, body, path, withAuth, isRetry } = options;

  const finalHeaders = { ...headers };
  if (withAuth) {
    const token = authStorage.get();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${API_PREFIX}${path}`, {
      method,
      headers: finalHeaders,
      credentials: "include",
      body,
    });
  } catch {
    throw new ApiError(
      "Could not reach the server. Check your internet connection and try again.",
      0,
      "NETWORK_ERROR"
    );
  }

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const isAuthRoute = path === "/auth/login" || path === "/auth/refresh";
    if (response.status === 401 && withAuth && !isRetry && !isAuthRoute) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        return rawRequest({ ...options, isRetry: true });
      }
      authStorage.clear();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    throw new ApiError(
      normalizeMessage(payload, `Request failed with status ${response.status}.`),
      response.status,
      normalizeCode(payload),
      payload
    );
  }

  return payload;
}

/** Shared fetch + in-flight deduplication + micro-cache + 401-refresh-and-retry handling. */
async function rawRequest(options: RawOptions): Promise<unknown> {
  const { method, path, withAuth, isRetry } = options;

  if (method !== "GET") {
    responseCache.clear();
    return executeRawRequest(options);
  }

  if (isRetry) {
    return executeRawRequest(options);
  }

  const key = `${withAuth ? "auth:" : "anon:"}${path}`;

  const cached = responseCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.payload;
  }

  const existingInflight = inflightGets.get(key);
  if (existingInflight) {
    return existingInflight;
  }

  const promise = executeRawRequest(options)
    .then((payload) => {
      responseCache.set(key, { payload, timestamp: Date.now() });
      return payload;
    })
    .finally(() => {
      inflightGets.delete(key);
    });

  inflightGets.set(key, promise);
  return promise;
}

function unwrap<T>(payload: unknown, raw: boolean): T {
  if (raw) return payload as T;
  if (payload && typeof payload === "object" && "data" in (payload as Record<string, unknown>)) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

/**
 * Centralized HTTP client. Reads the base URL from config, attaches the auth
 * token to every request, unwraps the backend's { success, data } / { success:
 * false, error } envelope, and transparently refreshes an expired access
 * token once before giving up.
 */
export async function http<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, headers, withAuth = true, raw = false, _isRetry = false } = options;
  const payload = await rawRequest({
    method,
    path,
    withAuth,
    isRetry: _isRetry,
    headers: { "Content-Type": "application/json", ...(headers ?? {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return unwrap<T>(payload, raw);
}

/**
 * Multipart upload — no Content-Type header (the browser sets the
 * multipart boundary itself when given a FormData body; setting it
 * manually breaks the boundary).
 */
export async function httpUpload<T>(path: string, formData: FormData): Promise<T> {
  const payload = await rawRequest({
    method: "POST",
    path,
    withAuth: true,
    isRetry: false,
    headers: {},
    body: formData,
  });
  return unwrap<T>(payload, false);
}

/**
 * Fetches raw binary blob data with authorization and token refresh support.
 */
export async function httpBlob(path: string, options: { withAuth?: boolean } = {}): Promise<Blob> {
  const { withAuth = true } = options;
  const headers: Record<string, string> = {};
  if (withAuth) {
    const token = authStorage.get();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${API_PREFIX}${path}`, { headers });
  } catch {
    throw new ApiError(
      "Could not reach the server. Check your internet connection and try again.",
      0,
      "NETWORK_ERROR"
    );
  }

  if (response.status === 401 && withAuth) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const newToken = authStorage.get();
      if (newToken) headers.Authorization = `Bearer ${newToken}`;
      response = await fetch(`${API_BASE_URL}${API_PREFIX}${path}`, { headers });
    }
  }

  if (!response.ok) {
    let msg = `Failed to load file (${response.status})`;
    try {
      const err = await response.json();
      if (err.error?.message) msg = err.error.message;
    } catch {
      // ignore
    }
    throw new ApiError(msg, response.status, "BLOB_FETCH_FAILED");
  }

  return response.blob();
}
